import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CreditCard, 
  Wallet,
  Save,
  CheckCircle,
  AlertCircle,
  Shield,
  TestTube,
  Zap,
  Search,
  History,
  DollarSign,
  Clock,
  XCircle,
  Building2,
  TrendingUp
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

function PaymentSettingsPage() {
  const { t, isRTL } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Stripe settings
  const [stripeActive, setStripeActive] = useState(true);
  const [stripeMode, setStripeMode] = useState('sandbox');
  const [stripeLiveSecretKey, setStripeLiveSecretKey] = useState('');
  const [stripeLivePublishableKey, setStripeLivePublishableKey] = useState('');
  const [stripeTestSecretKey, setStripeTestSecretKey] = useState('');
  const [stripeTestPublishableKey, setStripeTestPublishableKey] = useState('');
  
  // PayPal settings
  const [paypalActive, setPaypalActive] = useState(false);
  const [paypalMode, setPaypalMode] = useState('sandbox');
  const [paypalClientId, setPaypalClientId] = useState('');
  const [paypalSecret, setPaypalSecret] = useState('');
  
  // CoinPayUp settings
  const [coinpayupActive, setCoinpayupActive] = useState(false);
  const [coinpayupApiKey, setCoinpayupApiKey] = useState('');
  const [coinpayupEmail, setCoinpayupEmail] = useState('');
  const [coinpayupPassword, setCoinpayupPassword] = useState('');
  
  // Payment history
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  
  useEffect(() => {
    fetchSettings();
    fetchPaymentHistory();
  }, []);
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/payment-gateways`, {
        withCredentials: true
      });
      
      const data = response.data;
      setStripeActive(data.stripe_active ?? true);
      setStripeMode(data.stripe_mode || 'sandbox');
      setStripeLiveSecretKey(data.stripe_live_secret_key || '');
      setStripeLivePublishableKey(data.stripe_live_publishable_key || '');
      setStripeTestSecretKey(data.stripe_test_secret_key || '');
      setStripeTestPublishableKey(data.stripe_test_publishable_key || '');
      setPaypalActive(data.paypal_active ?? false);
      setPaypalMode(data.paypal_mode || 'sandbox');
      setPaypalClientId(data.paypal_client_id || '');
      setPaypalSecret(data.paypal_secret || '');
      setCoinpayupActive(data.coinpayup_active ?? false);
      setCoinpayupApiKey(data.coinpayup_api_key || '');
      setCoinpayupEmail(data.coinpayup_email || '');
      setCoinpayupPassword(data.coinpayup_password || '');
    } catch (error) {
      console.error('Failed to fetch payment settings:', error);
      showMessage('error', 'Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPaymentHistory = async (filters = {}) => {
    setHistoryLoading(true);
    try {
      const params = {
        limit: 50,
        search: filters.search || searchQuery || '',
        payment_method: filters.payment_method || paymentMethodFilter || 'all',
        start_date: filters.start_date || startDate || '',
        end_date: filters.end_date || endDate || ''
      };
      
      // Remove empty params
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === 'all') delete params[key];
      });
      
      const response = await axios.get(`${API}/superadmin/payment-history`, {
        params,
        withCredentials: true
      });
      
      setTransactions(response.data.transactions || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch payment history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };
  
  const handleSearch = () => {
    fetchPaymentHistory({
      search: searchQuery,
      payment_method: paymentMethodFilter,
      start_date: startDate,
      end_date: endDate
    });
  };
  
  const handleClearFilters = () => {
    setSearchQuery('');
    setPaymentMethodFilter('all');
    setStartDate('');
    setEndDate('');
    fetchPaymentHistory({
      search: '',
      payment_method: 'all',
      start_date: '',
      end_date: ''
    });
  };
  
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };
  
  const handleSave = async () => {
    setSaving(true);
    
    try {
      await axios.put(`${API}/superadmin/payment-gateways`, {
        stripe_active: stripeActive,
        stripe_mode: stripeMode,
        stripe_live_secret_key: stripeLiveSecretKey,
        stripe_live_publishable_key: stripeLivePublishableKey,
        stripe_test_secret_key: stripeTestSecretKey,
        stripe_test_publishable_key: stripeTestPublishableKey,
        paypal_active: paypalActive,
        paypal_mode: paypalMode,
        paypal_client_id: paypalClientId,
        paypal_secret: paypalSecret,
        coinpayup_active: coinpayupActive,
        coinpayup_api_key: coinpayupApiKey,
        coinpayup_email: coinpayupEmail,
        coinpayup_password: coinpayupPassword
      }, { withCredentials: true });
      
      showMessage('success', 'Payment settings saved successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save payment settings');
    } finally {
      setSaving(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      failed: { color: 'bg-red-100 text-red-700', icon: XCircle },
      expired: { color: 'bg-gray-100 text-gray-700', icon: XCircle }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="payment-settings-page">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Payment Settings</h2>
        <p className="text-gray-600 mt-1">Configure payment gateways and view payment history</p>
      </div>
      
      {/* Status Message */}
      {message.text && (
        <div className={`
          flex items-center gap-2 p-4 rounded-lg
          ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : ''}
          ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : ''}
        `}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}
      
      <Tabs defaultValue="gateways" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="gateways" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payment Gateways
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Payment History
          </TabsTrigger>
        </TabsList>
        
        {/* Payment Gateways Tab */}
        <TabsContent value="gateways" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Stripe Settings */}
            <Card className="shadow-lg">
              <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-purple-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <CreditCard className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle>Stripe</CardTitle>
                      <CardDescription>Accept card payments</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="stripe-active" className="text-sm">{t("common.active")}</Label>
                    <Switch
                      id="stripe-active"
                      checked={stripeActive}
                      onCheckedChange={setStripeActive}
                      data-testid="stripe-active-switch"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select value={stripeMode} onValueChange={setStripeMode}>
                    <SelectTrigger data-testid="stripe-mode-select">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">
                        <div className="flex items-center gap-2">
                          <TestTube className="w-4 h-4 text-orange-500" />
                          Sandbox (Test)
                        </div>
                      </SelectItem>
                      <SelectItem value="live">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-green-500" />
                          Live (Production)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="stripe-test-secret-key" className="flex items-center gap-2">
                    <TestTube className="w-4 h-4 text-orange-500" />
                    Test Secret Key
                  </Label>
                  <Input
                    id="stripe-test-secret-key"
                    type="password"
                    value={stripeTestSecretKey}
                    onChange={(e) => setStripeTestSecretKey(e.target.value)}
                    placeholder="sk_test_..."
                    data-testid="stripe-test-secret-key-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="stripe-test-publishable-key" className="flex items-center gap-2">
                    <TestTube className="w-4 h-4 text-orange-500" />
                    Test Publishable Key
                  </Label>
                  <Input
                    id="stripe-test-publishable-key"
                    type="text"
                    value={stripeTestPublishableKey}
                    onChange={(e) => setStripeTestPublishableKey(e.target.value)}
                    placeholder="pk_test_..."
                    data-testid="stripe-test-publishable-key-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="stripe-live-secret-key" className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-500" />
                    Live Secret Key
                  </Label>
                  <Input
                    id="stripe-live-secret-key"
                    type="password"
                    value={stripeLiveSecretKey}
                    onChange={(e) => setStripeLiveSecretKey(e.target.value)}
                    placeholder="sk_live_..."
                    data-testid="stripe-live-secret-key-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="stripe-live-publishable-key" className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-500" />
                    Live Publishable Key
                  </Label>
                  <Input
                    id="stripe-live-publishable-key"
                    type="text"
                    value={stripeLivePublishableKey}
                    onChange={(e) => setStripeLivePublishableKey(e.target.value)}
                    placeholder="pk_live_..."
                    data-testid="stripe-live-publishable-key-input"
                  />
                </div>
                
                <div className={`p-3 rounded-lg ${stripeActive ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${stripeActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className={`text-sm font-medium ${stripeActive ? 'text-green-700' : 'text-gray-500'}`}>
                      {stripeActive ? `Active (${stripeMode === 'live' ? 'Live' : 'Test'} mode)` : 'Inactive'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* PayPal Settings */}
            <Card className="shadow-lg">
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Wallet className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>PayPal</CardTitle>
                      <CardDescription>Accept PayPal payments</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="paypal-active" className="text-sm">{t("common.active")}</Label>
                    <Switch
                      id="paypal-active"
                      checked={paypalActive}
                      onCheckedChange={setPaypalActive}
                      data-testid="paypal-active-switch"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select value={paypalMode} onValueChange={setPaypalMode}>
                    <SelectTrigger data-testid="paypal-mode-select">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">
                        <div className="flex items-center gap-2">
                          <TestTube className="w-4 h-4 text-orange-500" />
                          Sandbox (Test)
                        </div>
                      </SelectItem>
                      <SelectItem value="live">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-green-500" />
                          Live (Production)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="paypal-client-id">Client ID</Label>
                  <Input
                    id="paypal-client-id"
                    type="text"
                    value={paypalClientId}
                    onChange={(e) => setPaypalClientId(e.target.value)}
                    placeholder="Your PayPal Client ID"
                    data-testid="paypal-client-id-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="paypal-secret">Secret Key</Label>
                  <Input
                    id="paypal-secret"
                    type="password"
                    value={paypalSecret}
                    onChange={(e) => setPaypalSecret(e.target.value)}
                    placeholder="Your PayPal Secret"
                    data-testid="paypal-secret-input"
                  />
                </div>
                
                <div className={`p-3 rounded-lg ${paypalActive ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${paypalActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className={`text-sm font-medium ${paypalActive ? 'text-green-700' : 'text-gray-500'}`}>
                      {paypalActive ? `Active (${paypalMode === 'live' ? 'Live' : 'Test'} mode)` : 'Inactive'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CoinPayUp Settings */}
            <Card className="shadow-lg">
              <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-yellow-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <svg className="w-6 h-6 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.5 8h5a2 2 0 010 4h-5m5 0a2 2 0 010 4h-5m0-8v8m0-8V6m0 10v2" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle>CoinPayUp</CardTitle>
                      <CardDescription>Accept cryptocurrency payments</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="coinpayup-active" className="text-sm">{t("common.active")}</Label>
                    <Switch
                      id="coinpayup-active"
                      checked={coinpayupActive}
                      onCheckedChange={setCoinpayupActive}
                      data-testid="coinpayup-active-switch"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="coinpayup-email">CoinPayUp Email</Label>
                    <Input
                      id="coinpayup-email"
                      type="email"
                      value={coinpayupEmail}
                      onChange={(e) => setCoinpayupEmail(e.target.value)}
                      placeholder="your@email.com"
                      data-testid="coinpayup-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="coinpayup-password">CoinPayUp Password</Label>
                    <Input
                      id="coinpayup-password"
                      type="password"
                      value={coinpayupPassword}
                      onChange={(e) => setCoinpayupPassword(e.target.value)}
                      placeholder="Your CoinPayUp password"
                      data-testid="coinpayup-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coinpayup-api-key">API Key / Token (optional)</Label>
                  <Input
                    id="coinpayup-api-key"
                    type="password"
                    value={coinpayupApiKey}
                    onChange={(e) => setCoinpayupApiKey(e.target.value)}
                    placeholder="Auto-generated from login, or paste manually"
                    data-testid="coinpayup-api-key-input"
                  />
                  <p className="text-xs text-gray-500">If email/password are provided, the token is auto-refreshed on each transaction. Get your account at <a href="https://coinpayup.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">coinpayup.com</a></p>
                </div>
                
                <div className={`p-3 rounded-lg ${coinpayupActive ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${coinpayupActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <span className={`text-sm font-medium ${coinpayupActive ? 'text-green-700' : 'text-gray-500'}`}>
                      {coinpayupActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-payment-settings"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
          
          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-900">Important Notes</h4>
                  <ul className="mt-2 text-sm text-blue-700 space-y-1">
                    <li>• Only <strong>active</strong> payment methods will be shown to admins</li>
                    <li>• Use <strong>Sandbox mode</strong> for testing before going live</li>
                    <li>• Stripe test keys start with <code className="bg-blue-100 px-1 rounded">sk_test_</code></li>
                    <li>• Stripe live keys start with <code className="bg-blue-100 px-1 rounded">sk_live_</code></li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Payment History Tab */}
        <TabsContent value="history" className="space-y-6">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Card className="border-2 border-indigo-200">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-indigo-100">
                      <History className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{summary.total_transactions}</p>
                      <p className="text-xs text-gray-500">Total Transactions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-2 border-green-200">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-green-100">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{summary.completed_count}</p>
                      <p className="text-xs text-gray-500">Completed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-2 border-yellow-200">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-yellow-100">
                      <Clock className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{summary.pending_count}</p>
                      <p className="text-xs text-gray-500">{t("common.pending")}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Revenue by Currency Cards */}
              {summary.revenue_by_currency && Object.entries(summary.revenue_by_currency).map(([currency, data]) => (
                <Card key={currency} className="border-2 border-emerald-200">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-emerald-100">
                        <TrendingUp className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {currency} {data.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </p>
                        <p className="text-xs text-gray-500">{data.count} completed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Transactions Table */}
          <Card className="shadow-lg">
            <CardHeader className="border-b bg-gray-50">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5 text-indigo-600" />
                      Admin Payment History
                    </CardTitle>
                    <CardDescription>All balance top-up transactions by admins</CardDescription>
                  </div>
                </div>
                
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Search Company</Label>
                    <Input
                      placeholder="Company name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      data-testid="search-transactions"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Payment Method</Label>
                    <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                      <SelectTrigger data-testid="payment-method-filter">
                        <SelectValue placeholder="All Methods" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Methods</SelectItem>
                        <SelectItem value="stripe">Stripe</SelectItem>
                        <SelectItem value="paypal">PayPal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="start-date-filter"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      data-testid="end-date-filter"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button onClick={handleSearch} className="flex-1" data-testid="apply-filters-btn">
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                    <Button variant="outline" onClick={handleClearFilters} data-testid="clear-filters-btn">
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Transaction ID</TableHead>
                      <TableHead className="font-semibold">Company</TableHead>
                      <TableHead className="font-semibold">Admin</TableHead>
                      <TableHead className="font-semibold">Method</TableHead>
                      <TableHead className="font-semibold text-right">{t("payments.amount")}</TableHead>
                      <TableHead className="font-semibold">{t("common.status")}</TableHead>
                      <TableHead className="font-semibold">{t("common.date")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                          No payment transactions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((txn) => (
                        <TableRow key={txn.transaction_id} data-testid={`txn-row-${txn.transaction_id}`}>
                          <TableCell className="font-mono text-sm">{txn.transaction_id}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{txn.company_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{txn.admin_name}</p>
                              <p className="text-xs text-gray-500">{txn.admin_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              <CreditCard className="w-3 h-3" />
                              {txn.payment_method?.toUpperCase() || 'STRIPE'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-600">
                            +{txn.currency} {txn.amount?.toFixed(2)}
                          </TableCell>
                          <TableCell>{getStatusBadge(txn.payment_status)}</TableCell>
                          <TableCell className="text-sm text-gray-600">{formatDate(txn.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default PaymentSettingsPage;
